import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Send, ArrowRight, ArrowLeft } from "lucide-react";
import { useTranslation } from "@/i18n/translations";
import GreenApiConnect from "@/components/GreenApiConnect";
import ZernioInstagram from "@/components/ZernioInstagram";
import { API } from "@/App";

const TZ_LIST = ["Europe/Paris", "Europe/London", "Europe/Berlin", "Europe/Madrid", "Europe/Rome",
  "Europe/Moscow", "Europe/Kyiv", "Europe/Istanbul", "Asia/Dubai", "Asia/Almaty", "America/New_York"];

const PLANS = [
  { key: "starter", name: "Starter", price: 39, limit: "100" },
  { key: "professional", name: "Professional", price: 79, limit: "1000", rec: true },
  { key: "unlimited", name: "Unlimited", price: 199, limit: "∞" },
];

export default function OnboardingPage() {
  const { t, lang, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [salon, setSalon] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // step 1
  const [name, setName] = useState("");
  const [bizType, setBizType] = useState("beauty");
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Paris");
  // step 2
  const [plan, setPlan] = useState("professional");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoErr, setPromoErr] = useState("");
  // step 3
  const [info, setInfo] = useState("");
  // step 5
  const [msgs, setMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/onboarding/funnel`);
        if (r.data.completed) { navigate("/calendar"); return; }
        setSalon(r.data);
        setName(r.data.salon_name || "");
        setBizType(r.data.business_type || "beauty");
        setInfo(r.data.salon_info || "");
        if (r.data.timezone) setTz(r.data.timezone);
        const urlStep = parseInt(params.get("step") || "0");
        const s = urlStep >= 1 && urlStep <= 5 ? urlStep : (r.data.step || 1);
        setStep(s);
        if (params.get("payment") === "success") toast.success(t("ob.cardOk"));
      } catch (e) {
        navigate("/calendar");
      } finally { setLoading(false); }
    })();
  }, []);  // eslint-disable-line


  // Promo: auto-pickup from registration link (?promo=) or saved discount
  useEffect(() => {
    if (step !== 2 || promo) return;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const stored = user?.id ? localStorage.getItem(`promocode_${user.id}`) : null;
      if (stored) {
        const p = JSON.parse(stored);
        setPromo(p); setPromoInput(p.code || "");
        return;
      }
      const pending = localStorage.getItem("chatity_promo");
      if (pending) { setPromoInput(pending); applyPromo(pending); }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line
  }, [step]);

  const applyPromo = async (codeArg) => {
    const code = String(codeArg || promoInput || "").trim().toUpperCase();
    if (!code) return;
    setPromoErr("");
    try {
      const r = await axios.post(`${API}/promocodes/validate`, { code, plan });
      setPromo(r.data);
      setPromoInput(code);
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (user?.id && r.data.promo_type !== "free_months") {
          localStorage.setItem(`promocode_${user.id}`, JSON.stringify(r.data));
        }
        localStorage.removeItem("chatity_promo");
      } catch (e) { /* ignore */ }
      toast.success(`${t("ob.promoAuto")}: ${code}`);
    } catch (e) {
      setPromo(null);
      setPromoErr(e.response?.data?.detail || "Invalid code");
    }
  };

  const clearPromo = () => {
    setPromo(null); setPromoInput(""); setPromoErr("");
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user?.id) localStorage.removeItem(`promocode_${user.id}`);
    } catch (e) { /* ignore */ }
  };

  const promoAppliesTo = (pKey) => promo && promo.promo_type !== "free_months"
    && ((promo.applicable_plans || ["all"]).includes("all") || (promo.applicable_plans || []).includes(pKey));

  const redeemFreeMonths = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/promocodes/redeem`, { code: promo.code });
      toast.success(r.data.message, { duration: 8000 });
      clearPromo();
      goTo(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const goTo = async (s) => {
    setStep(s);
    window.scrollTo({ top: 0 });
    try { await axios.put(`${API}/onboarding/funnel/progress`, { step: s }); } catch (e) { /* ignore */ }
  };

  const skipAll = async () => {
    if (!window.confirm(t("ob.skipConfirm"))) return;
    try { await axios.put(`${API}/onboarding/funnel/progress`, { step, completed: true }); } catch (e) { /* ignore */ }
    navigate("/calendar");
  };

  const finish = async () => {
    try { await axios.put(`${API}/onboarding/funnel/progress`, { step: 5, completed: true }); } catch (e) { /* ignore */ }
    toast.success(t("ob.launched"));
    navigate("/calendar");
  };

  const saveStep1 = async () => {
    if (!name.trim()) { toast.error(t("ob.nameRequired")); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/salons/${salon.salon_id}`, { salon_name: name.trim(), business_type: bizType });
      await axios.put(`${API}/team/timezone`, { timezone: tz }).catch(() => {});
      goTo(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const startCheckout = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/stripe/create-checkout`, {
        plan, origin_url: window.location.origin,
        trial_days: 7, return_path: "/onboarding?step=3",
        promocode: promoAppliesTo(plan) ? promo.code : undefined,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe error");
      setBusy(false);
    }
  };




  const saveStep3 = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/salons/${salon.salon_id}`, { salon_info: info });
      goTo(4);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const widgetCode = salon ? `<script src="${window.location.origin}/api/widget/embed.js?id=${salon.salon_id}"></script>` : "";

  const sendTest = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    setChatInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setChatBusy(true);
    try {
      const r = await axios.post(`${API}/chat/message`, {
        salon_id: salon.salon_id,
        session_id: `onboarding_${salon.salon_id}`,
        message: text,
        user_language: lang,
      });
      setMsgs((m) => [...m, { role: "ai", text: r.data.message }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "ai", text: "⚠️ " + (e.response?.data?.detail || "Error") }]);
    } finally {
      setChatBusy(false);
      setTimeout(() => chatRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 100);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#faf9f6]"><div className="text-gray-400">…</div></div>;

  const STEPS = [1, 2, 3, 4, 5];

  return (
    <div className="min-h-screen bg-[#faf9f6] text-gray-900" data-testid="onboarding-page">
      <header className="max-w-2xl mx-auto px-4 pt-6 pb-2 flex items-center justify-between">
        <img src="/logo-header.png" alt="Chatity" className="h-7 w-auto" />
        <button data-testid="ob-skip-all" onClick={skipAll} className="text-sm text-gray-400 hover:text-gray-700 underline">
          {t("ob.skip")}
        </button>
      </header>

      <div className="max-w-2xl mx-auto px-4 pb-28">
        {/* progress */}
        <div className="flex items-center gap-2 my-5" data-testid="ob-progress">
          {STEPS.map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-gray-900" : "bg-gray-200"}`} />
          ))}
        </div>
        <div className="text-xs text-gray-400 mb-4">{t("ob.stepOf").replace("{a}", step).replace("{b}", "5")}</div>

        {/* ── STEP 1: about ── */}
        {step === 1 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="ob-step1">
            <h1 className="text-2xl font-bold mb-1">{t("ob.s1Title")}</h1>
            <p className="text-gray-500 text-sm mb-5">{t("ob.s1Sub")}</p>
            <div className="space-y-4">
              <div><Label>{t("ob.language")}</Label>
                <div className="flex gap-2 mt-1" data-testid="ob-lang-switch">
                  {[["ru", "Русский"], ["en", "English"], ["fr", "Français"]].map(([code, label]) => (
                    <button key={code} data-testid={`ob-lang-${code}`} onClick={() => setLanguage(code)}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${lang === code ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 bg-white text-gray-700 hover:border-gray-400"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div><Label>{t("ob.salonName")} *</Label>
                <Input data-testid="ob-name" value={name} onChange={(e) => setName(e.target.value)} className="h-10" /></div>
              <div><Label>{t("ob.bizType")}</Label>
                <select data-testid="ob-biztype" value={bizType} onChange={(e) => setBizType(e.target.value)}
                  className="w-full h-10 border border-gray-200 rounded-md px-3 bg-white">
                  <option value="beauty">{t("settings.businessTypes.beauty")}</option>
                  <option value="medical">{t("settings.businessTypes.medical")}</option>
                  <option value="services">{t("settings.businessTypes.services")}</option>
                  <option value="general">{t("settings.businessTypes.general")}</option>
                </select></div>
              <div><Label>{t("ob.timezone")}</Label>
                <select data-testid="ob-tz" value={tz} onChange={(e) => setTz(e.target.value)}
                  className="w-full h-10 border border-gray-200 rounded-md px-3 bg-white">
                  {!TZ_LIST.includes(tz) && <option value={tz}>{tz}</option>}
                  {TZ_LIST.map((z) => <option key={z} value={z}>{z}</option>)}
                </select></div>
            </div>
            <Button data-testid="ob-s1-next" onClick={saveStep1} disabled={busy}
              className="mt-6 w-full h-11 bg-gray-900 text-white hover:bg-gray-800 font-semibold">
              {t("ob.next")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* ── STEP 2: card / trial ── */}
        {step === 2 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="ob-step2">
            <h1 className="text-2xl font-bold mb-1">{t("ob.s2Title")}</h1>
            <p className="text-gray-500 text-sm mb-4">{t("ob.s2Sub")}</p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-900 mb-5">
              ✅ {t("ob.s2Note")}
            </div>
            <div className="space-y-2 mb-4">
              {PLANS.map((p) => (
                <button key={p.key} data-testid={`ob-plan-${p.key}`} onClick={() => setPlan(p.key)}
                  className={`w-full flex items-center justify-between border rounded-xl px-4 py-3 text-left transition-colors ${plan === p.key ? "border-gray-900 bg-gray-50 ring-1 ring-gray-900" : "border-gray-200 hover:border-gray-400"}`}>
                  <div>
                    <span className="font-semibold">{p.name}</span>
                    {p.rec && <span className="ml-2 text-[11px] bg-gray-900 text-white rounded-full px-2 py-0.5">{t("ob.recommended")}</span>}
                    <div className="text-xs text-gray-500">{p.limit} {t("ob.dialogsMo")}</div>
                  </div>
                  <div className="font-bold text-right">
                    {promoAppliesTo(p.key) ? (
                      <>
                        <span className="line-through text-gray-400 font-normal text-sm mr-1.5">€{p.price}</span>
                        <span className="text-emerald-700">€{(p.price * (1 - (promo.discount_percent || 0) / 100)).toFixed(0)}</span>
                      </>
                    ) : <>€{p.price}</>}
                    <span className="text-xs text-gray-400 font-normal">/{t("ob.mo")}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Promo code */}
            <div className="mb-5" data-testid="ob-promo-block">
              {promo ? (
                <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-300 rounded-lg px-3 py-2.5">
                  <div className="text-sm">
                    <span className="font-bold text-emerald-900">{promo.code}</span>
                    {promo.promo_type === "free_months" ? (
                      <span className="ml-2 bg-amber-500 text-white text-xs rounded-full px-2 py-0.5">🎁 {promo.free_months} {t("ob.promoFree")}</span>
                    ) : (
                      <span className="ml-2 bg-emerald-600 text-white text-xs rounded-full px-2 py-0.5">−{promo.discount_percent}%</span>
                    )}
                  </div>
                  <button data-testid="ob-promo-clear" onClick={clearPromo} className="text-gray-400 hover:text-red-500 text-sm">✕</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input data-testid="ob-promo-input" placeholder={t("ob.promo")} value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoErr(""); }}
                    onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                    className="h-9 uppercase" />
                  <Button data-testid="ob-promo-apply" onClick={() => applyPromo()} variant="outline" className="h-9 whitespace-nowrap">{t("ob.promoApply")}</Button>
                </div>
              )}
              {promoErr && <div className="text-xs text-red-600 mt-1" data-testid="ob-promo-error">{promoErr}</div>}
            </div>

            {promo?.promo_type === "free_months" && (
              <Button data-testid="ob-promo-redeem" onClick={redeemFreeMonths} disabled={busy}
                className="w-full h-11 mb-2 bg-amber-500 text-white hover:bg-amber-600 font-semibold">
                🎁 {t("ob.promoRedeem").replace("{n}", promo.free_months)}
              </Button>
            )}
            <Button data-testid="ob-s2-card" onClick={startCheckout} disabled={busy}
              className="w-full h-11 bg-gray-900 text-white hover:bg-gray-800 font-semibold">
              {t("ob.s2Cta")}
            </Button>
            <div className="flex justify-between mt-3">
              <button data-testid="ob-s2-back" onClick={() => goTo(1)} className="text-sm text-gray-400 hover:text-gray-700 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />{t("ob.back")}</button>
              <button data-testid="ob-s2-later" onClick={() => goTo(3)} className="text-sm text-gray-500 hover:text-gray-800 underline">{t("ob.s2Later")}</button>
            </div>
          </div>
        )}

        {/* ── STEP 3: train AI ── */}
        {step === 3 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="ob-step3">
            <h1 className="text-2xl font-bold mb-1">{t("ob.s3Title")}</h1>
            <p className="text-gray-500 text-sm mb-5">{t("ob.s3Sub")}</p>

            <Label className="font-semibold">{t("ob.kbLabel")}</Label>
            <textarea data-testid="ob-kb" value={info} onChange={(e) => setInfo(e.target.value)} rows={8}
              placeholder={t("ob.kbPh")}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-gray-400" />

            <Button data-testid="ob-s3-next" onClick={saveStep3} disabled={busy}
              className="mt-6 w-full h-11 bg-gray-900 text-white hover:bg-gray-800 font-semibold">
              {t("ob.next")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <button data-testid="ob-s3-back" onClick={() => goTo(2)} className="mt-3 text-sm text-gray-400 hover:text-gray-700 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />{t("ob.back")}</button>
          </div>
        )}

        {/* ── STEP 4: channels ── */}
        {step === 4 && (
          <div className="space-y-4" data-testid="ob-step4">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h1 className="text-2xl font-bold mb-1">{t("ob.s4Title")}</h1>
              <p className="text-gray-500 text-sm">{t("ob.s4Sub")}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
              <GreenApiConnect />
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
              <ZernioInstagram API={API} />
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <div className="font-semibold mb-2">💬 {t("ob.widgetTitle")}</div>
              <div className="bg-gray-900 text-emerald-300 text-xs font-mono rounded-lg p-3 break-all">{widgetCode}</div>
              <Button data-testid="ob-widget-copy" variant="outline" className="mt-2 h-9"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(widgetCode);
                    toast.success(t("ob.copied"));
                  } catch (e) {
                    try {
                      const ta = document.createElement("textarea");
                      ta.value = widgetCode; document.body.appendChild(ta);
                      ta.select(); document.execCommand("copy"); ta.remove();
                      toast.success(t("ob.copied"));
                    } catch (e2) { toast.error("Copy failed"); }
                  }
                }}>
                <Copy className="w-3.5 h-3.5 mr-1.5" />{t("ob.widgetCopy")}
              </Button>
              <div className="mt-3 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2.5 text-sm text-sky-900" data-testid="ob-widget-nosite">
                ✨ {t("ob.widgetNoSite")}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <button data-testid="ob-s4-back" onClick={() => goTo(3)} className="text-sm text-gray-400 hover:text-gray-700 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />{t("ob.back")}</button>
              <Button data-testid="ob-s4-next" onClick={() => goTo(5)}
                className="h-11 px-8 bg-gray-900 text-white hover:bg-gray-800 font-semibold">
                {t("ob.next")} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 5: test & launch ── */}
        {step === 5 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="ob-step5">
            <h1 className="text-2xl font-bold mb-1">{t("ob.s5Title")}</h1>
            <p className="text-gray-500 text-sm mb-4">{t("ob.s5Sub")}</p>
            <div ref={chatRef} className="border border-gray-200 rounded-xl h-72 overflow-y-auto p-3 space-y-2 bg-gray-50 mb-3" data-testid="ob-test-chat">
              {msgs.length === 0 && <div className="text-center text-gray-400 text-sm mt-24">{t("ob.testHint")}</div>}
              {msgs.map((m, i) => (
                <div key={i} className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-gray-900 text-white" : "bg-white border border-gray-200"}`}>
                  {m.text}
                </div>
              ))}
              {chatBusy && <div className="bg-white border border-gray-200 rounded-2xl px-3.5 py-2 text-sm w-14 text-gray-400">…</div>}
            </div>
            <div className="flex gap-2">
              <Input data-testid="ob-test-input" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendTest()} placeholder={t("ob.testPh")} className="h-10" />
              <Button data-testid="ob-test-send" onClick={sendTest} disabled={chatBusy} className="h-10 bg-gray-900 text-white hover:bg-gray-800"><Send className="w-4 h-4" /></Button>
            </div>
            <Button data-testid="ob-finish" onClick={finish}
              className="mt-5 w-full h-12 bg-emerald-600 text-white hover:bg-emerald-700 font-bold text-base">
              🎉 {t("ob.finish")}
            </Button>
            <button data-testid="ob-s5-back" onClick={() => goTo(4)} className="mt-3 text-sm text-gray-400 hover:text-gray-700 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />{t("ob.back")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
